/* eslint-disable no-console */

'use client';

import { InternationalMarket } from '@chewy/market-handler';
import { getCookie } from 'cookies-next';
import { useEffect, useMemo } from 'react';

import { PAGE_TYPE } from '@/providers/AnalyticsProvider';
import { UserContextData } from '@/providers/types';
import { getMarketConfig } from '@/utils/marketConfig';
import { LoyaltyPageData } from '@/utils/ssr';

// Environment variable is intentionally lowercase to maintain consistency with existing codebase conventions
const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION || '';

interface PageData {
  pageType: string;
  'list-category': string;
  app_version: string;
  ecomm_pagetype: string;
  searchExperienceType: string;
  siteId?: string;
  isSheet?: boolean;
  sheetParentUrl?: string;
  sheetUrl?: string;
  screen_name?: string;
  deviceType?: string;
  devicePlatform?: string;
  browserType?: string;
  chewyAppVersion?: string;
  akamai_bot?: string;
  widgetSource?: string;
  isTelemedicineEligible?: boolean;
}

interface ClientAnalyticsProps {
  userContextData: UserContextData;
  analyticsToggles: any;
  experimentOverrides: any;
  market: string;
  pageData?: LoyaltyPageData;
  telemedicineEligible?: boolean;
}

interface AnalyticsConfiguration {
  analyticsToggles?: unknown;
}

interface ChewyAnalyticsData {
  akamaiBotHeader?: string;
  pageData?: PageData;
  segmentContextualData?: Record<string, unknown>;
}

declare global {
  interface Window {
    userContextData?: UserContextData;
    experimentOverrides?: unknown;
  }
}

export const ClientAnalytics = ({
  userContextData,
  experimentOverrides,
  analyticsToggles,
  market,
  pageData: loyaltyAnalyticsPageData,
  telemedicineEligible,
}: ClientAnalyticsProps) => {
  const pageData: PageData = useMemo(
    () => ({
      pageType: PAGE_TYPE,
      'list-category': PAGE_TYPE,
      app_version: `SPA-${APP_VERSION}`,
      ecomm_pagetype: PAGE_TYPE,
      searchExperienceType: '',
      ...(loyaltyAnalyticsPageData ?? {}),
      isTelemedicineEligible: telemedicineEligible,
    }),
    [loyaltyAnalyticsPageData, telemedicineEligible],
  );

  // WARNING: Using a static siteId can be a security risk. It is recommended to dynamically generate or retrieve the siteId to prevent potential vulnerabilities.
  if (market.includes(InternationalMarket.enCA)) {
    pageData.siteId = getMarketConfig(market).siteId;
  }

  useEffect(() => {
    if (typeof window === 'undefined') {
      console.error('Failed to init analytics due to missing window');
      return;
    }

    window.userContextData = userContextData;
    window.experimentOverrides = experimentOverrides;
    const chewyConf: AnalyticsConfiguration = window.chewyConf ?? {};
    chewyConf.analyticsToggles = analyticsToggles;
    window.chewyConf = chewyConf;

    // analytics will fall back to the values in window.chewy.pageData if we don't include them as extra.
    // Passing the pageData object to window.chewy will ensure all events inherit these data
    const chewy: ChewyAnalyticsData = window.chewy ?? {};
    chewy.pageData = pageData;
    chewy.segmentContextualData = {
      ...chewy.segmentContextualData,
      isTelemedicineEligible: telemedicineEligible,
    };
    if (pageData.akamai_bot) {
      chewy.akamaiBotHeader = pageData.akamai_bot;
    }
    window.chewy = chewy;

    if (userContextData) {
      userContextData.nmaLinkerId = userContextData.nmaLinkerId || getCookie('nmaLinkerId') || '';
    }

    const isBot = Boolean(pageData.akamai_bot);
    const analyticsData = {
      pageData,
      userData: userContextData,
      experimentOverrides,
      analyticsFeatureToggles: analyticsToggles,
      isBot,
      botHeader: isBot ? pageData.akamai_bot : null,
    };

    void Promise.all([
      import('storefront-analytics/dist/spa-analytics'),
      import('storefront-analytics/dist/auto-promo'),
      import('storefront-analytics/dist/auto-footer'),
    ])
      .then(([spaAnalytics, autoPromoAnalytics, autoFooterAnalytics]) => {
        spaAnalytics.init(analyticsData);
        autoPromoAnalytics.default();
        autoFooterAnalytics.default();
      })
      .catch(error => {
        // Analytics boot should not block or crash the product experience.
        console.warn('Failed to initialize storefront analytics', error);
      });
  }, [
    analyticsToggles,
    experimentOverrides,
    userContextData,
    pageData,
    loyaltyAnalyticsPageData,
    telemedicineEligible,
  ]);

  return null;
};
